import React, { useEffect, useState } from "react";
import type { SaveStatus } from "../hooks/useCloudAutosave";
import { TITLE_BAR_LEFT, TITLE_BAR_MAX_WIDTH } from "./editorTitleBarLayout";

/** Coarse, lazily-updated "time ago" label for the last successful save. */
export const formatRelativeSavedAt = (
  savedAt: number,
  now: number = Date.now(),
): string => {
  const seconds = Math.max(0, Math.round((now - savedAt) / 1000));
  if (seconds < 5) {
    return "just now";
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
};

export interface SaveStatusLabel {
  text: string;
  tone: string;
}

/**
 * The user-facing wording for each save status.
 *
 * "Offline" is reserved for actually being offline (see data/saveFailure.ts):
 * a server that answered with an error gets an error label, because telling a
 * plainly-online user "will sync" when nothing will ever sync is how a total
 * save outage stayed invisible for days.
 */
export const saveStatusLabel = (
  status: SaveStatus,
  savedAt: number | null,
  now: number = Date.now(),
  hasUnsavedChanges: boolean = false,
): SaveStatusLabel | null => {
  // Unsaved edits outrank a stale "Saved · 37s ago", which is the label the
  // owner was reading while deciding whether it was safe to close the tab.
  if (hasUnsavedChanges && status !== "saving" && !isFailureStatus(status)) {
    return { text: "Unsaved changes", tone: "#6b7280" };
  }
  switch (status) {
    case "saving":
      return { text: "Saving…", tone: "#6b7280" };
    case "offline":
      return { text: "Offline — kept locally, will sync", tone: "#b45309" };
    case "error":
      return { text: "Couldn't save — retrying", tone: "#b45309" };
    case "failed":
      return {
        text: "Save failed — your work is kept locally",
        tone: "#b91c1c",
      };
    case "saved":
      return savedAt === null
        ? null
        : {
            text: `Saved · ${formatRelativeSavedAt(savedAt, now)}`,
            tone: "#6b7280",
          };
    default:
      return null;
  }
};

/** A status where the user is owed the real reason, not just the short label. */
const isFailureStatus = (status: SaveStatus) =>
  status === "offline" || status === "error" || status === "failed";

export interface SaveStatusChipContent {
  label: SaveStatusLabel | null;
  /** Hover text for the status chip (`null` when there is no chip). */
  labelTitle: string | null;
  /** Persistent note about images that can never be saved. */
  warning: string | null;
}

/**
 * Everything the chip shows, decided in one pure place.
 *
 * Two facts used to be computed and then dropped on the floor: the failure's
 * actual cause (a save could stop working forever while the chip said only
 * "Save failed — your work is kept locally") and the "images too large, skipped"
 * warning from an otherwise successful save. The cause is appended to the chip's
 * hover text — the chip is one narrow line, and the short label is what should
 * stay glanceable — while the warning gets its own line, because it is advice
 * the user must act on and no status transition will repeat it.
 */
export const saveStatusChipContent = ({
  status,
  savedAt,
  errorMessage = null,
  imageWarning = null,
  now = Date.now(),
  hasUnsavedChanges = false,
}: {
  status: SaveStatus;
  savedAt: number | null;
  errorMessage?: string | null;
  imageWarning?: string | null;
  now?: number;
  hasUnsavedChanges?: boolean;
}): SaveStatusChipContent => {
  const label = saveStatusLabel(status, savedAt, now, hasUnsavedChanges);
  return {
    label,
    labelTitle: !label
      ? null
      : isFailureStatus(status) && errorMessage && errorMessage !== label.text
      ? `${label.text} — ${errorMessage}`
      : label.text,
    warning: imageWarning || null,
  };
};

// Sits directly under the file title in the top-left column (see
// editorTitleBarLayout), clear of the centred toolbar. Vertically it threads a
// 27px band: the title chip ends at y=49 and Excalidraw's left properties
// island begins at y=76 — hence the tight top offset and padding below.
const chipStyle: React.CSSProperties = {
  position: "absolute",
  top: "3.25rem",
  left: TITLE_BAR_LEFT,
  maxWidth: TITLE_BAR_MAX_WIDTH,
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.35rem",
  pointerEvents: "none",
};

/**
 * Unobtrusive save-status indicator sitting just under the title chip:
 * `Saving…` / `Saved · <relative>` / `Offline — kept locally, will sync` /
 * `Couldn't save — retrying`, plus a non-blocking multi-tab conflict note.
 * Relative time refreshes lazily.
 */
export const SaveStatusChip: React.FC<{
  status: SaveStatus;
  savedAt: number | null;
  hasConflict: boolean;
  /** Images permanently skipped by an otherwise successful save. */
  imageWarning?: string | null;
  /** Why the last save failed (shown as the chip's hover text). */
  errorMessage?: string | null;
  /** Edits not in the cloud yet: debounce pending, in flight, or last save failed. */
  hasUnsavedChanges?: boolean;
  /** Flush the pending save immediately. Absent ⇒ no Save now button. */
  onSaveNow?: () => void;
}> = ({
  status,
  savedAt,
  hasConflict,
  imageWarning,
  errorMessage,
  hasUnsavedChanges = false,
  onSaveNow,
}) => {
  const [, setTick] = useState(0);

  // Lazily refresh the relative-time label while showing a saved state.
  useEffect(() => {
    if (status !== "saved" || savedAt === null) {
      return;
    }
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, [status, savedAt]);

  const { label, labelTitle, warning } = saveStatusChipContent({
    status,
    savedAt,
    errorMessage,
    imageWarning,
    hasUnsavedChanges,
  });

  // Autosave stays the mechanism; this is the button that says so. Without it
  // the only way to save on demand was Ctrl+S — undiscoverable — so closing a
  // tab meant watching "Saved · 37s ago" and hoping. Offered exactly when there
  // is something to flush, so it never becomes a permanent nag.
  const canSaveNow = !!onSaveNow && hasUnsavedChanges && status !== "saving";

  if (!label && !warning && !hasConflict) {
    return null;
  }

  // Shared by the status line and the image warning below it.
  const chipTextStyle = (tone: string): React.CSSProperties => ({
    fontSize: "0.75rem",
    lineHeight: 1.25,
    fontWeight: 500,
    color: tone,
    background: "var(--island-bg-color, #fff)",
    padding: "0.15rem 0.55rem",
    borderRadius: "0.4rem",
    boxShadow: "var(--shadow-island, 0 1px 4px rgba(0,0,0,0.12))",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  });

  // The container's pointer-events:none inherits and kills `title` hovers —
  // re-enable them only on a status line whose title says more than its text.
  const labelRevealsMore = !!label && !!labelTitle && labelTitle !== label.text;

  return (
    <>
      {(label || warning) && (
        <div style={chipStyle} data-testid="save-status-chip">
          <span
            style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
          >
            {label && (
              <span
                data-testid="save-status-label"
                style={{
                  ...chipTextStyle(label.tone),
                  ...(labelRevealsMore ? { pointerEvents: "auto" } : null),
                }}
                title={labelTitle ?? ""}
              >
                {label.text}
              </span>
            )}
            {canSaveNow && (
              <button
                type="button"
                data-testid="save-now"
                onClick={onSaveNow}
                title="Save now (Ctrl/Cmd+S)"
                style={{
                  ...chipTextStyle("#4f46e5"),
                  border: "none",
                  cursor: "pointer",
                  // The container is pointer-events:none so the canvas stays
                  // draggable underneath; the button has to opt back in.
                  pointerEvents: "auto",
                }}
              >
                Save now
              </button>
            )}
          </span>
          {warning && (
            // Advice the user must act on and no status transition repeats it,
            // so it wraps instead of ellipsizing: readable without any hover.
            <span
              role="status"
              data-testid="save-image-warning"
              style={{
                ...chipTextStyle("#b45309"),
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "clip",
                overflowWrap: "anywhere",
              }}
            >
              {warning}
            </span>
          )}
        </div>
      )}
      {hasConflict && (
        // Rare and wordy: given its own line BELOW the toolbar (which ends at
        // ~3.75rem) rather than squeezed into the narrow top-left column.
        <div
          style={{
            position: "absolute",
            top: "4.75rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <span
            role="alert"
            style={{
              fontSize: "0.72rem",
              color: "#92400e",
              background: "#fef3c7",
              padding: "0.2rem 0.6rem",
              borderRadius: "0.4rem",
              whiteSpace: "nowrap",
            }}
          >
            This file was changed in another tab — reload to see latest
          </span>
        </div>
      )}
    </>
  );
};

export default SaveStatusChip;
